import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Printer,
  Copy,
  FileDown,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { showDeleteConfirm, showSuccess, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { ListLoader } from '@/components/shared/ListLoader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { fixedAssetService, FixedAsset, Lookup } from '@/services/fixedAssetService'

export const FixedAssetsList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [assets, setAssets] = useState<FixedAsset[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [deletingAssetId, setDeletingAssetId] = useState<number | null>(null)
  const [assetTypes, setAssetTypes] = useState<Lookup[]>([])
  const [selectedAssetCategory, setSelectedAssetCategory] = useState<string>('0') // '0' means "All"
  const [runMonthYear, setRunMonthYear] = useState<string>('')
  const [lastRunMonthYear, setLastRunMonthYear] = useState<string | null>(null)
  const [runMonthLocked, setRunMonthLocked] = useState(false)
  const [runningDepreciation, setRunningDepreciation] = useState(false)

  const branchId = user?.selectedBranch?.branchId

  // Load asset types lookup
  useEffect(() => {
    const loadAssetTypes = async () => {
      try {
        const types = await fixedAssetService.getAssetCategoryLookups()
        setAssetTypes(types || [])
      } catch (error) {
        console.error('Error loading asset types:', error)
      }
    }
    loadAssetTypes()
  }, [])

  // Load assets
  useEffect(() => {
    if (!branchId) {
      return
    }

    const loadAssets = async () => {
      try {
        setLoading(true)
        const response = await fixedAssetService.getAllAssets(branchId, pageNumber, pageSize)
        const list = response.data || []
        setAssets(list)
        setTotalCount(response.totalRecords || list.length || 0)
        const pages = response.totalRecords && response.pageSize ? Math.ceil(response.totalRecords / response.pageSize) : 0
        setTotalPages(pages || 1)
      } catch (error: any) {
        const status = error.response?.status
        const msg = (error.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setAssets([])
        setTotalCount(0)
        setTotalPages(1)
        if (!isNoData) {
          await showError('Error', error.response?.data?.message || 'Failed to load fixed assets', 5000)
        }
      } finally {
        setLoading(false)
      }
    }

    loadAssets()
  }, [branchId, pageNumber, pageSize])

  const loadDepreciationRunStatus = async () => {
    if (!branchId) return
    try {
      const status = await fixedAssetService.getDepreciationRunStatus(branchId)
      setLastRunMonthYear(status.lastMonthYear)
      setRunMonthLocked(status.hasRun)
      if (status.nextMonthYear) setRunMonthYear(status.nextMonthYear)
    } catch (error) {
      console.error('Error loading depreciation run status:', error)
    }
  }

  useEffect(() => {
    void loadDepreciationRunStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId])

  // Helper function to get asset type name by ID
  const getAssetTypeName = (assetTypeId?: number): string => {
    if (!assetTypeId) return 'N/A'
    const assetType = assetTypes.find(type => type.id === assetTypeId)
    if (assetType) {
      return i18n.language === 'ar' 
        ? (assetType.lookupNameAr || assetType.lookupNameEn || 'N/A')
        : (assetType.lookupNameEn || assetType.lookupNameAr || 'N/A')
    }
    return 'N/A'
  }

  // Asset category options for dropdown
  const assetCategoryOptions = useMemo(() => {
    const allOption = { value: '0', label: 'All' }
    const categoryOptions = assetTypes.map((type) => ({
      value: String(type.id),
      label: i18n.language === 'ar' 
        ? (type.lookupNameAr || type.lookupNameEn || '')
        : (type.lookupNameEn || type.lookupNameAr || ''),
    }))
    return [allOption, ...categoryOptions]
  }, [assetTypes, i18n.language])

  // Filter assets based on search query and asset category
  const filteredAssets = useMemo(() => {
    let filtered = assets

    // Filter by asset category
    if (selectedAssetCategory && selectedAssetCategory !== '0') {
      const categoryId = Number(selectedAssetCategory)
      filtered = filtered.filter((asset) => asset.assetCategoryId === categoryId)
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter((asset) => {
        return (
          (asset.name || '').toLowerCase().includes(query) ||
          (asset.fixedAssetNumber || '').toLowerCase().includes(query) ||
          (asset.assetCategoryName || '').toLowerCase().includes(query) ||
          (asset.serialNumber || '').toLowerCase().includes(query) ||
          String(asset.id || '').includes(query)
        )
      })
    }

    return filtered
  }, [assets, searchQuery, selectedAssetCategory])

  // Handle depreciation run
  const handleRunDepreciation = async () => {
    if (!branchId) {
      await showError('Error', t('fixedAssets.messages.branchNotSelected'), 5000)
      return
    }

      if (!runMonthYear) {
        await showError('Error', t('fixedAssets.messages.pleaseSelectMonthYear'), 5000)
        return
      }

    try {
      setRunningDepreciation(true)
      
      // Format date for API: "YYYY-MM" format (e.g., "2025-12")
      // runMonthYear is already in "YYYY-MM" format from the month input
      const monthYear = runMonthYear
      
      const response = await fixedAssetService.runDepreciation(branchId, monthYear)

      if (response.isError) {
        await showError('Error', response.message || t('fixedAssets.messages.failedToRunDepreciation'), 5000)
      } else {
        await showSuccess(t('fixedAssets.messages.depreciationRunCompleted'))
        const assetsResponse = await fixedAssetService.getAllAssets(branchId, pageNumber, pageSize)
        setAssets(assetsResponse.data || [])
        setTotalCount(assetsResponse.totalRecords || 0)
        await loadDepreciationRunStatus()
      }
    } catch (error: any) {
      console.error('Error running depreciation:', error)
      await showError('Error', error.response?.data?.message || error.message || 'Failed to run depreciation', 5000)
    } finally {
      setRunningDepreciation(false)
    }
  }

  const handleAddNew = () => {
    navigate('/fixed-assets/new')
  }

  const handleEdit = (asset: FixedAsset) => {
    const assetId = asset.id
    if (!assetId) {
      toast.error('Invalid asset ID')
      return
    }
    navigate(`/fixed-assets/${assetId}/edit`)
  }

  const handleDelete = async (asset: FixedAsset) => {
    const assetId = asset.id
    if (!assetId || !branchId) {
      toast.error('Invalid asset ID')
      return
    }

    const assetName = asset.name || 'this asset'
    const confirmed = await showDeleteConfirm(assetName, `Are you sure you want to delete "${assetName}"? This action cannot be undone.`)
    if (!confirmed) {
      return
    }

    try {
      setDeletingAssetId(assetId)
      const response = await fixedAssetService.deleteAsset(assetId)
      
      // Handle response: API returns 200 OK on success
      if (response && response.success !== false) {
        await showSuccess('Asset Deleted', 'Fixed asset has been deleted successfully.', 3000)
        // Reload assets
        const listResponse = await fixedAssetService.getAllAssets(branchId, pageNumber, pageSize)
        setAssets(listResponse.data || [])
        setTotalCount(listResponse.totalRecords || 0)
      } else {
        await showError('Delete Failed', response?.message || 'Failed to delete asset', 5000)
      }
    } catch (error: any) {
      await showError('Delete Failed', error.response?.data?.message || 'Failed to delete asset', 5000)
    } finally {
      setDeletingAssetId(null)
    }
  }

  const handlePrintList = () => {
    window.print()
  }

  const csvEscape = (value: any) => {
    const s = value === null || value === undefined ? '' : String(value)
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
    return s
  }

  const handleCopy = async () => {
    try {
      const headers = [
        'ID',
        'Asset No',
        'Name',
        'Category',
        'Purchase Date',
        'Serial No',
        'Purchase Price',
      ]

      const rows = filteredAssets.map((a) => [
        a.id ?? '',
        a.fixedAssetNumber ?? '',
        a.name ?? '',
        a.assetCategoryName || getAssetTypeName(a.assetCategoryId),
        formatDate(a.purchaseDate),
        a.serialNumber ?? '',
        formatCurrency(a.purchasePrice),
      ])

      const text = [headers, ...rows].map((r) => r.join('\t')).join('\n')
      await navigator.clipboard.writeText(text)
      toast.success('Copied to clipboard')
    } catch (e) {
      console.error('Copy failed', e)
      toast.error('Copy failed')
    }
  }

  const handleExportCSV = () => {
    const headers = [
      'ID',
      'Asset No',
      'Name',
      'Category',
      'Purchase Date',
      'Serial No',
      'Purchase Price',
    ]

    const rows = filteredAssets.map((a) => [
      a.id ?? '',
      a.fixedAssetNumber ?? '',
      a.name ?? '',
      a.assetCategoryName || getAssetTypeName(a.assetCategoryId),
      formatDate(a.purchaseDate),
      a.serialNumber ?? '',
      a.purchasePrice ?? 0,
    ])

    const csv = [headers, ...rows].map((r) => r.map(csvEscape).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `fixed-assets-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      })
    } catch {
      return dateString
    }
  }

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('fixedAssets.title')}
        description={t('fixedAssets.addNewAsset')}
        action={{
          label: t('fixedAssets.addNew'),
          onClick: handleAddNew,
          icon: Plus,
        }}
      />

      {/* Depreciation Run Section */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full sm:w-auto">
              <Label htmlFor="assetCategory">{t('fixedAssets.assetCategory')}</Label>
              <SearchableSelect
                options={assetCategoryOptions}
                value={selectedAssetCategory}
                onValueChange={(value) => setSelectedAssetCategory(value || '0')}
                placeholder={t('common.all')}
                searchPlaceholder={t('fixedAssets.placeholders.searchCategory')}
                emptyMessage={t('fixedAssets.placeholders.noCategoryFound')}
              />
            </div>
            <div className="flex-1 w-full sm:w-auto">
              <Label htmlFor="runMonthYear">{t('fixedAssets.runMonthYear')}</Label>
              <Input
                id="runMonthYear"
                type="month"
                value={runMonthYear}
                onChange={(e) => {
                  if (!runMonthLocked) setRunMonthYear(e.target.value)
                }}
                readOnly={runMonthLocked}
                disabled={runMonthLocked}
                placeholder={t('fixedAssets.runMonthYear')}
              />
              {lastRunMonthYear ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('fixedAssets.messages.nextRunMustFollowLast', {
                    last: lastRunMonthYear,
                    next: runMonthYear,
                  })}
                </p>
              ) : null}
            </div>
            <div className="w-full sm:w-auto">
              <Button
                onClick={handleRunDepreciation}
                disabled={runningDepreciation || !runMonthYear}
                className="w-full sm:w-auto"
              >
                {runningDepreciation ? t('fixedAssets.running') : t('fixedAssets.run')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <label className="text-sm font-medium mb-2 block">{t('fixedAssets.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('fixedAssets.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button variant="outline" size="sm" onClick={handleCopy} disabled={filteredAssets.length === 0} className="w-full sm:w-auto">
                <Copy className="h-4 w-4 mr-2" />
                {t('common.copy')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={filteredAssets.length === 0} className="w-full sm:w-auto">
                <FileDown className="h-4 w-4 mr-2" />
                {t('common.csv')}
              </Button>
              <Button variant="outline" size="sm" onClick={handlePrintList} className="w-full sm:w-auto">
                <Printer className="h-4 w-4 mr-2" />
                {t('common.print')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <ListLoader message={t('fixedAssets.loadingAssets')} />
          ) : filteredAssets.length === 0 ? (
            <EmptyState
              title={t('fixedAssets.noAssetsFound')}
              description={
                searchQuery
                  ? t('common.tryAdjustingSearch')
                  : 'Get started by adding your first fixed asset to the system.'
              }
              action={
                !searchQuery
                  ? {
                      label: t('fixedAssets.addNewAsset'),
                      onClick: handleAddNew,
                    }
                  : undefined
              }
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('fixedAssets.columns.name')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('fixedAssets.columns.assetType')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('fixedAssets.columns.purchaseDate')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('fixedAssets.columns.warrantyDate')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('fixedAssets.columns.purchasePrice')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[120px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('fixedAssets.columns.action')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAssets.map((asset) => (
                      <TableRow
                        key={asset.id}
                        className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                      >
                        <TableCell className="px-4 py-2 text-sm text-center align-middle font-medium">
                          {asset.name || 'N/A'}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {getAssetTypeName(asset.assetCategoryId)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {formatDate(asset.purchaseDate)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {formatDate(asset.warrantyExpiry)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {formatCurrency(asset.purchasePrice)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-center align-middle">
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(asset)}
                              className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(asset)}
                              disabled={deletingAssetId === asset.id}
                              className="h-7 w-7 p-0 hover:bg-destructive/10 dark:hover:bg-destructive/20 text-destructive"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="border-t px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Showing {filteredAssets.length} of {totalCount} entries
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(prev => Math.max(1, prev - 1))}
                      disabled={pageNumber === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">
                      Page {pageNumber} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(prev => Math.min(totalPages, prev + 1))}
                      disabled={pageNumber >= totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

